/**
 * CatalogFeaturesMenu — katalog sayfasının 3 nokta menüsü.
 *
 * Öğeler JSX yerine veri (`FeatureMenuEntry`) olarak tutuluyor; böylece kullanılabilir
 * olanlar başa, pasifler sona sıralanabiliyor. JSX ile yazıldığında bu sıra elle
 * korunmak zorunda kalıyor ve koşullu pasif öğeler yanlış yere düşüyor.
 *
 * Düzen 2 sütunlu: dar ekranda tek sütuna iner, menü ekran dışına taşmaz.
 */

import React from 'react';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CallToActionIcon from '@mui/icons-material/CallToAction';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

interface FeatureMenuEntry {
  key: string;
  label: string;
  secondary?: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  hidden?: boolean;
}

export interface CatalogFeaturesMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  bulkActionMode: boolean;
  exporting: boolean;
  loading: boolean;
  onOpenTaxonomy: () => void;
  onToggleBulkActionMode: () => void;
  onOpenTableSettings: () => void;
  onExportToExcel: () => void;
  onOpenImportModal: () => void;
}

const CatalogFeaturesMenu: React.FC<CatalogFeaturesMenuProps> = ({
  anchorEl,
  open,
  onClose,
  bulkActionMode,
  exporting,
  loading,
  onOpenTaxonomy,
  onToggleBulkActionMode,
  onOpenTableSettings,
  onExportToExcel,
  onOpenImportModal,
}) => {
  const entries: FeatureMenuEntry[] = [
    {
      key: 'taxonomy',
      label: 'Sektör / Kategori Yönetimi',
      icon: <AccountTreeIcon fontSize="small" />,
      onClick: onOpenTaxonomy,
    },
    {
      key: 'bulkActions',
      label: bulkActionMode ? 'Toplu İşlem Modundan Çık' : 'Toplu İşlem Modu',
      icon: <CallToActionIcon fontSize="small" color={bulkActionMode ? 'warning' : 'primary'} />,
      onClick: onToggleBulkActionMode,
    },
    {
      key: 'customizeTable',
      label: 'Tabloyu Özelleştir',
      icon: <ViewColumnIcon fontSize="small" />,
      onClick: onOpenTableSettings,
    },
    {
      key: 'exportExcel',
      label: exporting ? 'Hazırlanıyor...' : "Excel'e Aktar",
      secondary: 'Aktif filtredeki kayıtlar',
      icon: <FileDownloadIcon fontSize="small" />,
      onClick: onExportToExcel,
      disabled: exporting || loading,
    },
    {
      key: 'importExcel',
      label: "Excel'den İçe Aktar",
      secondary: 'Yeni ürünleri toplu ekle',
      icon: <FileUploadIcon fontSize="small" />,
      onClick: onOpenImportModal,
    },
  ];

  const visibleEntries = entries.filter((entry) => !entry.hidden);
  // Kullanılabilir öğeler önce, pasifler en sonda — her iki grup içinde yukarıdaki
  // yazım sırası korunur.
  const orderedEntries = [
    ...visibleEntries.filter((entry) => !entry.disabled),
    ...visibleEntries.filter((entry) => entry.disabled),
  ];

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { maxHeight: '75vh', maxWidth: 'calc(100vw - 24px)' } }}
      MenuListProps={{
        sx: {
          display: 'grid',
          // xs: tek sütun (ekran genişliği yetmiyor) · sm+: iki sütun
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(240px, 1fr))' },
          columnGap: 0.5,
          py: 0.5,
        },
      }}
    >
      {orderedEntries.map((entry) => (
        <MenuItem
          key={entry.key}
          disabled={entry.disabled}
          onClick={
            entry.onClick
              ? () => {
                  onClose();
                  entry.onClick!();
                }
              : undefined
          }
          sx={
            entry.disabled
              ? { opacity: 0.6, '&.Mui-disabled': { color: 'text.secondary' } }
              : undefined
          }
        >
          <ListItemIcon>{entry.icon}</ListItemIcon>
          <ListItemText primary={entry.label} secondary={entry.secondary} />
        </MenuItem>
      ))}
    </Menu>
  );
};

export default CatalogFeaturesMenu;
